import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal } from '../../../../components/modals';
import classnames from 'classnames';
import styles from '../../styles.module.scss';
import { NifiEdgeData } from '../types';
import { NifiForm } from './nifi-edge-form';
import { InPageSpinner } from '@components/spinners/in-page-spinner';
import { DeleteNifiEdge } from './confirm';
import { UpdateConfigConfirmModal } from './update-config-confirm';
import { DataSourceHandler } from '@api/data-source-handler';
import { SecretManager, SecretManagerHandler } from '@api/secret-manager-handler';
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import { useSelector } from 'react-redux';
import { RootState } from '@store/types';
import { uuid } from 'uuidv4';
import { errorHandlerFor400 } from '@api/services/errorhandler';
import { DeleteFilledIcon } from '@pages/workflow-analytics-page/assets/icons';

type NifiEdgeModalProps = {
    showModal: boolean;
    toggleClose: () => void;
    getNifiData: () => void;
    nifidata: NifiEdgeData | null
};


const NifiEdgeModal: React.FC<NifiEdgeModalProps> = ({
    showModal,
    toggleClose,
    nifidata,
    getNifiData
}) => {
    const [showLoader, toggleLoader] = useState(false);
    const [showDelLoader, toggleDelLoader] = useState(false);
    const [showDeletePopup, toggleDeletePopup] = useState(false);
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const { activeSecretManagers } = useSelector((store: RootState) => store.SecretManagerReducer);
    const [secretsList, setSecretsList] = useState<string[]>([]);
    const [secretsLoading, setSecretsLoading] = useState(false);
    const [originalRdbmsValues, setOriginalRdbmsValues] = useState<any>(null);
    const [isUpdatingCredentials, setIsUpdatingCredentials] = useState(false);
    const intervalTimerForCredentials = useRef<NodeJS.Timeout | null>(null);
    const [showUpdateConfigModal, setShowUpdateConfigModal] = useState(false);
    const [pendingFormValues, setPendingFormValues] = useState<any>(null);

    const fetchSecretsList = useCallback(() => {
        if (activeSecretManagers.length > 0 && Env?.REACT_APP_PLATFORM_URL) {
            setSecretsLoading(true);
            const firstActiveManager = activeSecretManagers?.filter((manager: SecretManager) => manager.active)?.[0];
            SecretManagerHandler.GetSecrets(
                `${Env?.REACT_APP_PLATFORM_URL}/vault/v1`,
                firstActiveManager.id,
                (res) => {
                    const secretNames = res?.keys || [];
                    setSecretsList(secretNames);
                    setSecretsLoading(false);
                },
                () => {
                    setSecretsLoading(false);
                }
            );
        }
    }, [activeSecretManagers, Env?.REACT_APP_PLATFORM_URL]);

    useEffect(() => {
        if (showModal) {
            fetchSecretsList();
            // Store original RDBMS values for change detection
            const relationalNodeKinds = ['Relational', 'OPCUA', 'MQTT'];
            if (nifidata && relationalNodeKinds.includes(nifidata.nodeKind)) {
                const connectionDetails = (nifidata as any).connectionDetails || {};
                setOriginalRdbmsValues({
                    rdbmsHost: connectionDetails.rdbmsHost || (nifidata as any).rdbmsHost,
                    rdbmsPort: connectionDetails.rdbmsPort || (nifidata as any).rdbmsPort,
                    rdbmsDatabase: connectionDetails.rdbmsDatabase || (nifidata as any).rdbmsDatabase,
                    rdbmsUser: connectionDetails.rdbmsUser || (nifidata as any).rdbmsUser,
                    rdbmsPassword: connectionDetails.rdbmsPassword || (nifidata as any).rdbmsPassword,
                    rdbmsConnectionUrl: connectionDetails.rdbmsConnectionUrl || (nifidata as any).rdbmsConnectionUrl
                });
            }
            // Check if there's an active polling session and resume it
            const activePollingKey = `activePolling_${nifidata?.nodeName}`;
            const activePolling = localStorage.getItem(activePollingKey);
            if (activePolling) {
                const { reqID, nodeName } = JSON.parse(activePolling);
                if (!intervalTimerForCredentials.current) {
                    intervalTimerForCredentials.current = setInterval(
                        () => pollCredentialsUpdate(reqID, nodeName),
                        4000
                    );
                }
            }
        } else {
            // Clear original values when modal closes
            setOriginalRdbmsValues(null);
        }
    }, [showModal, fetchSecretsList, nifidata]);

    const updateConfigStatus = (nodeName: string, status: 'running' | 'success' | 'failed') => {
        const statusKey = `configUpdateStatus_${nodeName}`;
        if (status === 'success' || status === 'failed') {
            // Store final status for 5 minutes, then auto-clear
            localStorage.setItem(statusKey, status);
            setTimeout(() => {
                localStorage.removeItem(statusKey);
                getNifiData();
            }, 300000); // 5 minutes
        } else {
            localStorage.setItem(statusKey, status);
        }
    };

    const pollCredentialsUpdate = (reqID: string, nodeName: string) => {
        DataSourceHandler.GetViewReqJson(
            `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`,
            reqID,
            (res) => {
                if (res?.state === "SUCCESS" || res?.state === "STAGED") {
                    if (intervalTimerForCredentials.current) {
                        clearInterval(intervalTimerForCredentials.current);
                        intervalTimerForCredentials.current = null;
                    }
                    // Clear active polling from localStorage
                    const activePollingKey = `activePolling_${nodeName}`;
                    localStorage.removeItem(activePollingKey);
                    setIsUpdatingCredentials(false);
                    updateConfigStatus(nodeName, 'success');
                    getNifiData();
                }
                if (res?.state === "FAILED") {
                    if (intervalTimerForCredentials.current) {
                        clearInterval(intervalTimerForCredentials.current);
                        intervalTimerForCredentials.current = null;
                    }
                    // Clear active polling from localStorage
                    const activePollingKey = `activePolling_${nodeName}`;
                    localStorage.removeItem(activePollingKey);
                    setIsUpdatingCredentials(false);
                    updateConfigStatus(nodeName, 'failed');
                    getNifiData();
                }
            }
        );
    };
    const handleUpdateWithConfig = () => {
        if (!pendingFormValues) return;

        setShowUpdateConfigModal(false);
        toggleLoader(true);

        const values = pendingFormValues;
        const { changedFields, edgePayload } = values;

        // Call CreateHierarchyRequest API
        const jsonData = {
            nodeName: values.edgeName,
            json: JSON.stringify({
                command: "update_credentials",
                parameters: changedFields,
                nodeName: values.edgeName
            }),
            state: "new",
            requestName: `update_credentials_${uuid()}`,
            sinkKind: "S3",
            sinkName: `defaultS3AfSink-${location.host.split('.')[0]}`,
            nodeKind: values.nodeKind,
        };

        // Set status to running
        updateConfigStatus(values.edgeName, 'running');

        DataSourceHandler.CreateHierarchyRequest(
            `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`,
            jsonData,
            (response: any) => {
                const reqID = response?.data?.id;
                if (reqID) {
                    // Store active polling info in localStorage
                    const activePollingKey = `activePolling_${values.edgeName}`;
                    localStorage.setItem(activePollingKey, JSON.stringify({
                        reqID,
                        nodeName: values.edgeName
                    }));
                    // Start polling for the credentials update in background
                    intervalTimerForCredentials.current = setInterval(
                        () => pollCredentialsUpdate(reqID, values.edgeName),
                        4000
                    );
                } else {
                    console.error('No reqID in response:', response);
                    updateConfigStatus(values.edgeName, 'failed');
                }
            },
            (error: any) => {
                errorHandlerFor400(error);
                updateConfigStatus(values.edgeName, 'failed');
            }
        );

        // Call UpdateEdgeNode API (don't wait for CreateHierarchyRequest)
        DataSourceHandler.UpdateEdgeNode(
            `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`,
            nifidata?.nodeName || '',
            edgePayload,
            () => {
                getNifiData();
                toggleLoader(false);
                successAlert("Edge node update initiated");
                toggleClose();
            },
            (e: any) => {
                errorAlert(e.data);
                toggleLoader(false);
            }
        );

        setPendingFormValues(null);
    };

    const handleUpdateOnly = () => {
        if (!pendingFormValues) return;

        setShowUpdateConfigModal(false);
        toggleLoader(true);

        const values = pendingFormValues;
        const { edgePayload } = values;

        // Only call UpdateEdgeNode API
        DataSourceHandler.UpdateEdgeNode(
            `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`,
            nifidata?.nodeName || '',
            edgePayload,
            () => {
                getNifiData();
                toggleLoader(false);
                successAlert("Successfully updated edge node");
                toggleClose();
            },
            (e: any) => {
                errorAlert(e.data);
                toggleLoader(false);
            }
        );

        setPendingFormValues(null);
    };

    const onSubmit = (values: any) => {
        const { secretFields } = values;

        // Check if this is an update for Relational node with changed RDBMS credentials
        const relationalNodeKinds = ['Relational', 'OPCUA', 'MQTT'];
        const isRelationalUpdate =
            nifidata && relationalNodeKinds.includes(values.nodeKind);

        let hasRdbmsChanges = false;
        const changedFields: any[] = [];

        if (isRelationalUpdate && originalRdbmsValues) {
            // Helper function to extract actual value from secret field structure
            const getActualValue = (value: any) => {
                if (typeof value === 'object' && value !== null && 'data' in value) {
                    return value.data;
                }
                return value;
            };

            // Check for connection URL changes (host, port, database)
            const connectionUrlChanged =
                values.rdbmsHost !== originalRdbmsValues.rdbmsHost ||
                values.rdbmsPort !== originalRdbmsValues.rdbmsPort ||
                values.rdbmsDatabase !== originalRdbmsValues.rdbmsDatabase;

            if (connectionUrlChanged) {
                changedFields.push({
                    name: "connection-url",
                    value: values.rdbmsConnectionUrl
                });
                hasRdbmsChanges = true;
            }

            // Check for username change
            const originalUser = getActualValue(originalRdbmsValues.rdbmsUser);
            if (values.rdbmsUser !== originalUser) {
                changedFields.push({
                    name: "username",
                    value: values.rdbmsUser
                });
                hasRdbmsChanges = true;
            }

            // Check for password change
            const originalPassword = getActualValue(originalRdbmsValues.rdbmsPassword);
            if (values.rdbmsPassword !== originalPassword) {
                changedFields.push({
                    name: "password",
                    value: values.rdbmsPassword,
                    sensitive: true
                });
                hasRdbmsChanges = true;
            }
        }

        // Helper to ensure all 3 keys are present in secret field format
        const getSecretFieldValue = (fieldKey: string, fallbackValue: any) => {
            const secretField = secretFields?.[fieldKey];
            return {
                useSecret: secretField?.useSecret ?? false,
                data: secretField?.data ?? fallbackValue ?? '',
                secretManagerId: secretField?.secretManagerId ?? null
            };
        };

        // Build payload based on nodeKind
        const edgePayload: any = {
            "nodeName": values.edgeName,
            "nodeIp": "",
            "sinkKind": values.sinkKind,
            "sinkInformation": values.sinkInformation,
            "category": "Edge",
            nodeKind: values.nodeKind,
            enableScaling: values.enableScaling,
            scaleNodes: values.enableScaling ? values.scaleNodes : null,
            notificationEnabled: values.notificationEnabled,
            notificationEmail: values.notificationEmail,
            autoResumeRequests: values.autoResumeRequests,
            ip21ConnectionId: values.ip21ConnectionId,
            connectionDetails: {
                accessKey: getSecretFieldValue('accessKey', values.accessKey),
            }
        };
        if (values.nodeKind === 'AspenIP21') {
            edgePayload.connectionDetails.ip21Host = getSecretFieldValue('ip21Host', values.ip21Host);
            edgePayload.connectionDetails.ip21Port = getSecretFieldValue('ip21Port', values.ip21Port);
            edgePayload.connectionDetails.ip21User = getSecretFieldValue('ip21User', values.ip21User);
            edgePayload.connectionDetails.ip21Password = getSecretFieldValue('ip21Password', values.ip21Password);
        } else if (values.nodeKind === 'Relational') {
            edgePayload.connectionDetails.rdbmsType = getSecretFieldValue('rdbmsType', values.rdbmsType);
            edgePayload.connectionDetails.rdbmsHost = getSecretFieldValue('rdbmsHost', values.rdbmsHost);
            edgePayload.connectionDetails.rdbmsPort = getSecretFieldValue('rdbmsPort', values.rdbmsPort);
            edgePayload.connectionDetails.rdbmsUser = getSecretFieldValue('rdbmsUser', values.rdbmsUser);
            edgePayload.connectionDetails.rdbmsPassword = getSecretFieldValue('rdbmsPassword', values.rdbmsPassword);
            edgePayload.connectionDetails.rdbmsDatabase = getSecretFieldValue('rdbmsDatabase', values.rdbmsDatabase);
            edgePayload.connectionDetails.rdbmsConnectionUrl = getSecretFieldValue('rdbmsConnectionUrl', values.rdbmsConnectionUrl);
        } else if (values.nodeKind === 'Modbus') {
            edgePayload.connectionDetails.ip21Host = getSecretFieldValue('ip21Host', values.ip21Host);
            edgePayload.connectionDetails.ip21Port = getSecretFieldValue('ip21Port', values.ip21Port);
            edgePayload.connectionDetails.ip21User = getSecretFieldValue('ip21User', values.ip21User);
            edgePayload.connectionDetails.ip21Password = getSecretFieldValue('ip21Password', values.ip21Password);
        } else if (values.nodeKind === 'MQTT') {
            edgePayload.connectionDetails.mqttTransportProtocol = getSecretFieldValue('mqttTransportProtocol', values.mqttTransportProtocol);
            edgePayload.connectionDetails.mqttSslEnabled = getSecretFieldValue('mqttSslEnabled', values.secretFields?.mqttTransportProtocol?.data ==="TLS");
            edgePayload.connectionDetails.mqttHost = getSecretFieldValue('mqttHost', values.mqttHost);
            edgePayload.connectionDetails.mqttPort = getSecretFieldValue('mqttPort', values.mqttPort);
            // edgePayload.connectionDetails.mqttBrokerUri = getSecretFieldValue('mqttBrokerUri', values.mqttBrokerUri);
            edgePayload.connectionDetails.mqttUsername = getSecretFieldValue('mqttUsername', values.mqttUsername);
            edgePayload.connectionDetails.mqttPassword = getSecretFieldValue('mqttPassword', values.mqttPassword);
            edgePayload.connectionDetails.mqttTlsFilePath = getSecretFieldValue('mqttTlsFilePath', values.mqttTlsFilePath);
        } else if (values.nodeKind === 'OPCUA') {
            edgePayload.connectionDetails.opcuaTransportProtocol = getSecretFieldValue('opcuaTransportProtocol', values.opcuaTransportProtocol);
            edgePayload.connectionDetails.opcuaHost = getSecretFieldValue('opcuaHost', values.opcuaHost);
            edgePayload.connectionDetails.opcuaPort = getSecretFieldValue('opcuaPort', values.opcuaPort);
            edgePayload.connectionDetails.opcuaPath = getSecretFieldValue('opcuaPath', values.opcuaPath);
            edgePayload.connectionDetails.opcuaServerUrl = getSecretFieldValue('opcuaServerUrl', values.opcuaServerUrl);
            edgePayload.connectionDetails.opcuaMessageSecurityMode = getSecretFieldValue('opcuaMessageSecurityMode', values.opcuaMessageSecurityMode);
            edgePayload.connectionDetails.opcuaSecurityPolicy = getSecretFieldValue('opcuaSecurityPolicy', values.opcuaSecurityPolicy);
            edgePayload.connectionDetails.opcuaAuthenticationMode = getSecretFieldValue('opcuaAuthenticationMode', values.opcuaAuthenticationMode);
            edgePayload.connectionDetails.opcuaUsername = getSecretFieldValue('opcuaUsername', values.opcuaUsername);
            edgePayload.connectionDetails.opcuaPassword = getSecretFieldValue('opcuaPassword', values.opcuaPassword);
            edgePayload.connectionDetails.opcuaCertificate = getSecretFieldValue('opcuaCertificate', values.opcuaCertificate);
        }
        if (nifidata) {
            // If RDBMS credentials changed, show confirmation modal
            if (hasRdbmsChanges) {
                // Store form values and show confirmation modal
                setPendingFormValues({
                    ...values,
                    changedFields,
                    edgePayload
                });
                setShowUpdateConfigModal(true);
            } else {
                // Normal update without credential changes
                toggleLoader(true);
                DataSourceHandler.UpdateEdgeNode(
                    `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`,
                    nifidata.nodeName,
                    edgePayload,
                    () => {
                        getNifiData();
                        toggleLoader(false);
                        successAlert("Successfully updated edge node");
                        toggleClose();
                    },
                    (e: any) => {
                        errorAlert(e.data);
                        toggleLoader(false);
                    }
                );
            }
        } else {
            DataSourceHandler.CreateEdgeNode(`${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`, edgePayload, () => {
                getNifiData();
                toggleLoader(false);
                toggleClose();
                successAlert("Successfully created edge node")
            }, (e: any) => {
                errorAlert(e.data);
                toggleLoader(false);
            });
        }
    };

    const isNifiDataNull = nifidata === null;
    const onDelete = () => {
        toggleDelLoader(true);
        toggleDeletePopup(false);
        if (!isNifiDataNull && nifidata.nodeName) {
            DataSourceHandler.DeleteEdgeNode(`${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`, nifidata.nodeName, () => {
                getNifiData();
                toggleDelLoader(false);
                toggleClose();
            }, (e: any) => {
                toggleDelLoader(false);
                errorAlert(e.data)
            });
        }
    };
    const isDisabled = showLoader || showDelLoader || isUpdatingCredentials;
    return (
        <>
            <Modal
                isOpen={showModal}
                toggleClose={toggleClose}
                title={nifidata ? 'Edit Edge Information' : 'Add Edge Information'}
                subtitle={`Configure node fields. Toggle "Use Secret" to use values from the secret manager.`}
                showCloseMark={true}
                className={classnames(
                    'editConnection_container',
                    styles['addEditNifiModal'],
                    styles['addEditNifiModal--wide']
                )}
            >
                <div className={styles['addEditNifiModal__innerContainer']}>
                    <NifiForm
                        type={isNifiDataNull ? 'ADD' : 'UPDATE'}
                        onSubmit={onSubmit}
                        initialValues={nifidata}
                        secretsList={secretsList}
                        secretsLoading={secretsLoading}
                        secretManagerId={activeSecretManagers.length > 0 ? activeSecretManagers?.[0]?.id : null}
                        submitButtons={
                            <div className={styles['addEditNifiSinkModal__actionBtns']}>
                                <div>
                                    {!isNifiDataNull ? (
                                        <button
                                            className={classnames(
                                                'btn btn-lg btn-red',
                                                styles['nifi-delete-btn']
                                            )}
                                            type="button"
                                            disabled={isDisabled}
                                            onClick={() => {
                                                toggleDeletePopup(true);
                                            }}
                                        >
                                            <div className={styles['sink-delete-btn']}>
                                                <DeleteFilledIcon />
                                                {showDelLoader ? (
                                                    <InPageSpinner
                                                        size="XSmall"
                                                        color="black"
                                                        className={styles['auto']}
                                                    />
                                                ) : (
                                                    'Delete'
                                                )}
                                            </div>
                                        </button>
                                    ) : null}
                                </div>
                                <div>
                                    <button
                                        className={classnames(
                                            'btn btn-lg btn-yellow',
                                            styles['nifi-edit-btn']
                                        )}
                                        type="submit"
                                        disabled={isDisabled}
                                    >
                                        {showLoader ? (
                                            <InPageSpinner
                                                size="XSmall"
                                                color="black"
                                                className={styles['auto']}
                                            />
                                        ) : isNifiDataNull ? (
                                            'Create'
                                        ) : (
                                            'Update'
                                        )}
                                    </button>
                                    <button
                                        className={classnames(
                                            'btn btn-lg',
                                            styles['nifi-cancel-btn']
                                        )}
                                        type="button"
                                        onClick={toggleClose}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        }
                    />
                </div>
            </Modal>
            <DeleteNifiEdge
                onConfirm={onDelete}
                showModal={showDeletePopup}
                title={!isNifiDataNull ? `Are you sure you what to delete the ${nifidata.nodeName || ''} edge?` : ''}
                toggleClose={() => {
                    toggleDeletePopup(false);
                }}

            />
            <UpdateConfigConfirmModal
                showModal={showUpdateConfigModal}
                toggleClose={() => {
                    setShowUpdateConfigModal(false);
                    setPendingFormValues(null);
                }}
                onUpdateWithConfig={handleUpdateWithConfig}
                onUpdateOnly={handleUpdateOnly}
            />
        </>
    );
};

export { NifiEdgeModal };
